import React from 'react';
import styled from 'styled-components';
import { CreditOfferTypes } from 'types/CreditOfferTypes';
import { TypographyComponent, MarketingCard } from '@tvg/fuse-design';
import { renderContent, renderContentWithParams } from 'utilities/content/content-utilities';
import { PriceTerm } from 'types/Product';
import { sendAnalyticsEvent } from 'utilities/creditOffers/creditOfferUtilities';

const StyledMarketingCard = styled(MarketingCard)`
  margin-bottom: var(--spacing-space24);

  strong {
    color: var(--color-greys-grey10);
  }
`;

type Props = {
  creditOfferType: CreditOfferTypes;
  longestWeeklyPriceTermWithZeroInterest?: PriceTerm;
  onClickCallback: () => void;
};

const getCardHeading = (creditOfferType: Props['creditOfferType']): string => {
  switch (creditOfferType) {
    case CreditOfferTypes.STC_NO_OPTION_SELECTED:
      return 'littlewoods.card.stcNoOptionSelected.heading';
    case CreditOfferTypes.BNPL:
      return 'littlewoods.card.bnpl.heading';
    case CreditOfferTypes.STC:
      return 'littlewoods.card.stc.heading';
    default:
      return '';
  }
};

const getCardDescription = (creditOfferType: Props['creditOfferType']): string => {
  switch (creditOfferType) {
    case CreditOfferTypes.STC_NO_OPTION_SELECTED:
      return 'littlewoods.card.stcNoOptionSelected.description';
    case CreditOfferTypes.BNPL:
      return 'littlewoods.card.bnpl.description';
    case CreditOfferTypes.STC:
      return 'littlewoods.card.stc.description';
    default:
      return '';
  }
};

const getCardParams = (
  creditOfferType: Props['creditOfferType'],
  longestWeeklyPriceTermWithZeroInterest: Props['longestWeeklyPriceTermWithZeroInterest'],
): string[] => {
  const { termPeriod, weeklyPaymentAmount } = longestWeeklyPriceTermWithZeroInterest || {};

  if (!termPeriod || !weeklyPaymentAmount) return [];

  switch (creditOfferType) {
    case CreditOfferTypes.STC:
      return [termPeriod?.toString(), weeklyPaymentAmount];
    default:
      return [];
  }
};

const getAnalyticsLabel = (creditOfferType: Props['creditOfferType']): string => {
  switch (creditOfferType) {
    case CreditOfferTypes.STC_NO_OPTION_SELECTED || CreditOfferTypes.STC:
      return 'STC more info';
    case CreditOfferTypes.BNPL:
      return 'BNPL moreinfo';
    default:
      return '';
  }
};

function YouCouldPayCard(props: Props) {
  const { creditOfferType, longestWeeklyPriceTermWithZeroInterest, onClickCallback } = props;

  const renderHeader = () => (
    <TypographyComponent
      color="var(--color-brand-mid)"
      size={{ xxs: '1', l: '2' }}
      type="heading"
      dangerouslySetInnerHTML={{
        __html: renderContent(getCardHeading(creditOfferType)),
      }}
      data-testid="littlewoods_card_heading"
    />
  );

  const renderDescriptionMessage = () => (
    <TypographyComponent
      size={{ xxs: '-1', l: '1' }}
      type="paragraph"
      tag="span"
      dangerouslySetInnerHTML={{
        __html: renderContentWithParams({
          id: getCardDescription(creditOfferType),
          params: getCardParams(creditOfferType, longestWeeklyPriceTermWithZeroInterest),
        }),
      }}
      data-testid="littlewoods_card_description"
    />
  );

  const handleOnClick = (event: MouseEvent) => {
    event.preventDefault();
    onClickCallback();
    sendAnalyticsEvent({
      gaLabel: getAnalyticsLabel(creditOfferType),
    });
  };

  return (
    <StyledMarketingCard
      data-testid="littlewoods_card"
      header={renderHeader()}
      paragraphText={renderDescriptionMessage()}
      linkUrl="#"
      linkText={renderContent('littlewoods.card.link.text')}
      onClick={handleOnClick}
    />
  );
}

export default YouCouldPayCard;
